/**
 * Intersection Observer for Lazy Loading
 * Manages visibility detection for solution cards and other components
 */

export class LazyLoadManager {
  constructor(options = {}) {
    this.options = {
      rootMargin: '100px 0px', // Load 100px before entering viewport
      threshold: 0.1,
      ...options
    };

    // Support detection
    this.supported = (typeof window !== 'undefined') && ('IntersectionObserver' in window);

    // element -> callback
    this.callbacks = new Map();

    // element -> root (HTMLElement or null)
    this.elementToRoot = new Map();

    // root (HTMLElement or null) -> { observer: IntersectionObserver, elements: Set<HTMLElement> }
    this.observersByRoot = new Map();
  }

  /** Debug helpers (toggle with window.__WHT_LAZY_DEBUG = true) */
  debugEnabled() {
    try { return !!window.__WHT_LAZY_DEBUG; } catch { return false; }
  }

  _log(...args) {
    if (this.debugEnabled()) {
      try { console.debug('[LazyLoadManager]', ...args); } catch { /* noop */ }
    }
  }

  _describeRoot(root) {
    if (!root) return 'viewport';
    const id = root.id ? `#${root.id}` : '';
    const cls = root.className ? `.${String(root.className).split(' ').join('.')}` : '';
    const tag = root.tagName ? String(root.tagName).toLowerCase() : 'element';
    return `${tag}${id}${cls}`;
  }

  _isVisibleNow(element, root) {
    try {
      const rect = element.getBoundingClientRect();
      const rr = (root && root.getBoundingClientRect)
        ? root.getBoundingClientRect()
        : { top: 0, left: 0, right: window.innerWidth, bottom: window.innerHeight };
      // Take the first top margin from rootMargin as a simple px buffer
      const margin = parseInt((this.options.rootMargin || '0').split(' ')[0]) || 0;
      const withinVertical = rect.bottom >= (rr.top - margin) && rect.top <= (rr.bottom + margin);
      const withinHorizontal = rect.right >= rr.left && rect.left <= rr.right;
      return withinVertical && withinHorizontal;
    } catch {
      return false;
    }
  }

  /**
   * Find the nearest scrollable ancestor for an element.
   * Returns null to indicate viewport root when none found.
   */
  _getNearestScrollParent(element) {
    if (!element) return null;
    try {
      let el = element.parentElement;
      let preferredLayoutContent = null;      // prefer this when it is actually scrollable
      let firstScrollableNow = null;          // first ancestor that is currently scrollable
      let firstOverflowCandidate = null;      // any ancestor with overflow that could become scrollable

      while (el) {
        const style = window.getComputedStyle ? window.getComputedStyle(el) : null;
        const cls = el.classList || { contains: () => false };

        if (cls.contains('layout-content')) {
          preferredLayoutContent = preferredLayoutContent || el;
        }

        if (style) {
          const overflowY = style.overflowY || style.overflow;
          const overflow = style.overflow || 'visible';
          const overflowX = style.overflowX || style.overflow;
          const hasScrollBehavior = /(auto|scroll|overlay)/.test(`${overflow}${overflowX}${overflowY}`);
          if (hasScrollBehavior) {
            // Check if actually scrollable now (or forced scroll behavior)
            const scrollableNow = (el.scrollHeight > el.clientHeight) || (el.scrollWidth > el.clientWidth) || /scroll/.test(`${overflow}${overflowX}${overflowY}`);
            if (!firstOverflowCandidate) firstOverflowCandidate = el;
            if (!firstScrollableNow && scrollableNow) firstScrollableNow = el;
          }
        }
        el = el.parentElement;
      }

      // Prefer a scrollable `.layout-content` if present.
      if (preferredLayoutContent) {
        try {
          const st = window.getComputedStyle(preferredLayoutContent);
          const oy = st.overflowY || st.overflow;
          const ox = st.overflowX || st.overflow;
          const ov = st.overflow || 'visible';
          const hasScroll = /(auto|scroll|overlay)/.test(`${ov}${ox}${oy}`);
          const scrollableNow = (preferredLayoutContent.scrollHeight > preferredLayoutContent.clientHeight) || (preferredLayoutContent.scrollWidth > preferredLayoutContent.clientWidth);
          if (hasScroll || scrollableNow) {
            this._log('Using preferred root (.layout-content):', this._describeRoot(preferredLayoutContent));
            return preferredLayoutContent;
          }
        } catch { /* noop */ }
      }

      if (firstScrollableNow) {
        this._log('Using first scrollable ancestor:', this._describeRoot(firstScrollableNow));
        return firstScrollableNow;
      }

      if (firstOverflowCandidate) {
        this._log('Using overflow ancestor (fallback):', this._describeRoot(firstOverflowCandidate));
        return firstOverflowCandidate;
      }
    } catch (e) {
      // ignore, fall back to viewport
    }
    return null; // viewport root
  }

  /**
   * Get or create an IntersectionObserver for a specific root.
   */
  _getObserverForRoot(root) {
    const key = root || null;
    if (this.observersByRoot.has(key)) return this.observersByRoot.get(key).observer;

    if (!this.supported) return null;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this._log('Intersecting:', entry.target, 'in root:', this._describeRoot(root));
          const cb = this.callbacks.get(entry.target);
          if (cb) {
            cb(entry.target);
          }
          // Always unobserve once intersected to avoid repeated triggers
          this.unobserve(entry.target);
        }
      });
    }, { root, rootMargin: this.options.rootMargin, threshold: this.options.threshold });

    this.observersByRoot.set(key, { observer, elements: new Set() });
    return observer;
  }

  /**
   * Observe an element for lazy loading
   * @param {HTMLElement} element
   * @param {Function} callback - Called when element becomes visible
   */
  observe(element, callback) {
    if (!element || typeof callback !== 'function') return;

    if (!this.supported) {
      // Fallback: execute immediately if IntersectionObserver not supported
      callback(element);
      return;
    }

    const root = this._getNearestScrollParent(element);
    // Annotate for debugging
    try { if (element && element.dataset) element.dataset.lazyRoot = this._describeRoot(root); } catch { /* noop */ }
    this._log('Observe target:', element, 'with root:', this._describeRoot(root));

    // If already visible within the (root + margin) area, fire immediately
    if (this._isVisibleNow(element, root)) {
      this._log('Target already visible, invoking callback immediately.');
      try { callback(element); } catch { /* noop */ }
      return;
    }

    const observer = this._getObserverForRoot(root);
    if (!observer) {
      // Safety fallback
      callback(element);
      return;
    }

    this.callbacks.set(element, callback);
    this.elementToRoot.set(element, root || null);
    const bucket = this.observersByRoot.get(root || null);
    if (bucket) bucket.elements.add(element);
    observer.observe(element);
  }

  /**
   * Stop observing an element
   * @param {HTMLElement} element
   */
  unobserve(element) {
    const root = this.elementToRoot.get(element) || null;
    const bucket = this.observersByRoot.get(root);
    if (bucket && bucket.observer) {
      try { bucket.observer.unobserve(element); } catch (e) { /* noop */ }
      bucket.elements.delete(element);
      // Optional cleanup: if no elements left, disconnect and remove observer
      if (bucket.elements.size === 0) {
        try { bucket.observer.disconnect(); } catch (e) { /* noop */ }
        this.observersByRoot.delete(root);
      }
    }
    this.elementToRoot.delete(element);
    this.callbacks.delete(element);
  }

  /**
   * Cleanup all observations
   */
  disconnect() {
    this.observersByRoot.forEach(({ observer }) => {
      try { observer.disconnect(); } catch (e) { /* noop */ }
    });
    this.observersByRoot.clear();
    this.elementToRoot.clear();
    this.callbacks.clear();
  }
}
